(() => {
  const initialiseLoader = () => {
    const loader = document.querySelector('[data-bordi-loader]');
    if (!loader || loader.dataset.loaderReady === 'true') return;

    loader.dataset.loaderReady = 'true';
    const sessionKey = 'bordi-premium-loader-seen';
    const oncePerSession = loader.dataset.oncePerSession === 'true';
    const minimumDuration = Math.max(Number(loader.dataset.minDuration) || 900, 250);
    const startedAt = performance.now();
    let alreadySeen = false;

    try {
      alreadySeen = oncePerSession && window.sessionStorage.getItem(sessionKey) === 'true';
      if (!alreadySeen && oncePerSession) window.sessionStorage.setItem(sessionKey, 'true');
    } catch (error) {
      alreadySeen = false;
    }

    const finish = (immediate = false) => {
      if (loader.classList.contains('is-hidden')) return;
      const elapsed = performance.now() - startedAt;
      const wait = immediate ? 0 : Math.max(minimumDuration - elapsed, 0);

      window.setTimeout(() => {
        loader.classList.add('is-hidden');
        window.setTimeout(() => loader.remove(), 650);
      }, wait);
    };

    if (alreadySeen) {
      finish(true);
      return;
    }

    if (window.Shopify?.designMode) {
      window.setTimeout(() => finish(true), 300);
      return;
    }

    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', () => finish(), { once: true });

    window.setTimeout(() => finish(true), 4500);
  };

  const initialiseHeader = (scope = document) => {
    const header = scope.querySelector('.bordi-site-header');
    if (!header || header.dataset.headerReady === 'true') return;
    header.dataset.headerReady = 'true';

    const drawer = header.querySelector('.bordi-site-header__drawer');
    const drawerSummary = drawer?.querySelector(':scope > summary');
    const drawerNavigation = drawer?.querySelector(':scope > nav');
    const drawerClose = drawer?.querySelector('[data-bordi-mobile-menu-close]');
    const dropdowns = Array.from(header.querySelectorAll('.bordi-site-header__dropdown'));

    const setMobileMenuTop = () => {
      const boundary = header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--bordi-mobile-menu-top', `${Math.max(boundary, 0)}px`);
    };

    const closeMobileMenu = (restoreFocus = false) => {
      if (!drawer) return;
      drawer.removeAttribute('open');
      drawerSummary?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('bordi-mobile-menu-open');
      document.body.classList.remove('bordi-mobile-menu-open');
      if (restoreFocus) drawerSummary?.focus();
    };

    drawer?.addEventListener('toggle', () => {
      const isOpen = drawer.open;
      drawerSummary?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.documentElement.classList.toggle('bordi-mobile-menu-open', isOpen);
      document.body.classList.toggle('bordi-mobile-menu-open', isOpen);
      if (isOpen) {
        setMobileMenuTop();
        dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
        window.requestAnimationFrame(() => drawerNavigation?.focus({ preventScroll: true }));
      }
    });

    drawerClose?.addEventListener('click', () => closeMobileMenu(true));

    drawerNavigation?.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeMobileMenu(false);
    });

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        dropdowns.forEach((other) => {
          if (other !== dropdown) other.removeAttribute('open');
        });
      });
    });

    document.addEventListener('click', (event) => {
      dropdowns.forEach((dropdown) => {
        if (dropdown.open && !dropdown.contains(event.target)) dropdown.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeMobileMenu(true);
      dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
    });

    window.addEventListener('resize', () => {
      if (drawer?.open) setMobileMenuTop();
      if (window.innerWidth >= 750) closeMobileMenu(false);
    });
  };

  const initialiseAnnouncements = (scope = document) => {
    scope.querySelectorAll('[data-bordi-announcement]').forEach((announcement) => {
      if (announcement.dataset.announcementReady === 'true') return;
      announcement.dataset.announcementReady = 'true';

      const items = Array.from(announcement.querySelectorAll('[data-bordi-announcement-item]'));
      if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const speed = Math.max(Number(announcement.dataset.rotationSpeed) || 6000, 4000);
      let current = 0;
      let timer;

      const show = (index) => {
        items.forEach((item, itemIndex) => { item.hidden = itemIndex !== index; });
      };
      const start = () => {
        window.clearInterval(timer);
        timer = window.setInterval(() => {
          current = (current + 1) % items.length;
          show(current);
        }, speed);
      };

      show(current);
      start();
      announcement.addEventListener('mouseenter', () => window.clearInterval(timer));
      announcement.addEventListener('mouseleave', start);
      announcement.addEventListener('focusin', () => window.clearInterval(timer));
      announcement.addEventListener('focusout', start);
    });
  };

  const initialise = (scope = document) => {
    initialiseLoader();
    initialiseHeader(scope);
    initialiseAnnouncements(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
